import {itemDataQueries} from 'api';
import {SelectOptionsType} from 'global';
import {useDebounceEffect, useGetAllITemDataCursorPagination} from 'hooks';
import {ItemDataFilterRequest} from 'models/board';
import {useCallback, useMemo} from 'react';
import {AppControlledAutoComplete, AppControlledAutoCompleteProps} from './AutoComplete';

export interface AppItemDataSelectProps extends Omit<AppControlledAutoCompleteProps, 'options' | 'value'> {
    filters?: ItemDataFilterRequest;
}
export const AppItemDataSelect = ({disableClearable, filters, ...props}: AppItemDataSelectProps) => {
    const {
        getAllITemDataCursorPagination,
        getAllITemDataCursorPaginationLoading,
        getAllITemDataCursorPaginationResponse,
    } = useGetAllITemDataCursorPagination(itemDataQueries.GET_ALL_CURSOR_PAGINATION_FOR_SELECT_LIST);

    const handleNext = useCallback(() => {
        if (getAllITemDataCursorPaginationResponse?.pageInfo.hasNextPage) {
            getAllITemDataCursorPagination({
                variables: {
                    after: getAllITemDataCursorPaginationResponse?.pageInfo.endCursor,
                    first: 50,
                    filters: {
                        ...(filters ? filters : {}),
                    },
                },
            });
        }
    }, [, getAllITemDataCursorPaginationResponse?.pageInfo.endCursor]);

    useDebounceEffect(
        () => {
            getAllITemDataCursorPagination({
                variables: {
                    first: 50,
                    filters: {
                        ...(filters ? filters : {}),
                    },
                },
            });
        },
        [],
        500,
    );

    const options = useMemo(() => {
        if (getAllITemDataCursorPaginationResponse) {
            return getAllITemDataCursorPaginationResponse.nodes.map(x => {
                const urlSplit = x.url.split('/');
                let groupBy = '';
                if (urlSplit.length > 3) {
                    groupBy = urlSplit[2] + ' - ' + urlSplit[3];
                }
                const title = `#${x.ticketNo}: ${x.data.find(x => x.shouldUseAsTheFieldToCreateItem)?.value.value ?? ''}`;
                return {
                    value: x.id,
                    label: title,
                    groupBy: groupBy,
                } as SelectOptionsType;
            });
        }
        return [];
    }, [getAllITemDataCursorPaginationResponse]);

    // Sort options by groupBy property
    const sortedOptions = useMemo(() => {
        return [...options].sort((a, b) => {
            // Handle cases where groupBy might be empty
            if (!a.groupBy) return 1;
            if (!b.groupBy) return -1;
            return a.groupBy.localeCompare(b.groupBy);
        });
    }, [options]);

    return (
        <AppControlledAutoComplete
            descriptionText={
                'This section displays all open tickets. If you want to add a timer to a different ticket, you can do so manually using the time tracker option in the ticket details.'
            }
            groupBy={option => option.groupBy}
            loading={getAllITemDataCursorPaginationLoading}
            options={sortedOptions}
            disableClearable={disableClearable}
            onScrollBottomHandler={handleNext}
            {...props}
        />
    );
};

export default AppItemDataSelect;
